import { useState, useEffect, useRef, useCallback } from 'react';
import { isHmlEnvironment, isLocalDevelopment } from '@/utils/tenantDetection';
import { logger } from '@/utils/logger';

type HmlStatus = 'online' | 'offline' | null;

const HML_HEALTH_URL = 'https://api.hml.shelflix.com.br/trpc/health';
const POLL_INTERVAL_MS = 15_000;
const REQUEST_TIMEOUT_MS = 5_000;

/**
 * Polls the HML API health endpoint to determine environment availability.
 * Only active when running in the HML environment.
 * Returns null when not in HML (indicator should be hidden).
 */
export function useHmlStatus(): HmlStatus {
  const [status, setStatus] = useState<HmlStatus>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isHml = isHmlEnvironment() && !isLocalDevelopment();

  const checkHealth = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      const response = await fetch(HML_HEALTH_URL, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);
      setStatus(response.ok ? 'online' : 'offline');
    } catch {
      setStatus('offline');
    }
  }, []);

  useEffect(() => {
    if (!isHml) {
      setStatus(null);
      return;
    }

    logger.debug('HML status polling started');

    // Initial check
    checkHealth();

    // Start polling
    intervalRef.current = setInterval(checkHealth, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isHml, checkHealth]);

  return status;
}
